import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  IFileProcessingProgress,
  FileProcessingStatus,
  FileProcessingOperation,
  IDownloadProgress,
  ITranslationProgress,
  IPdfExportProgress,
  TypedFileProcessingProgress
} from '@newroom-connect/library/types';

import { BaseSSEProgressService } from '../sse/base-sse-progress.service';

@Injectable({
  providedIn: 'root'
})
export class FileProcessingService extends BaseSSEProgressService<IFileProcessingProgress> {
  // Alias for better semantic naming
  public get processingProgress(): BehaviorSubject<IFileProcessingProgress[] | null> {
    return this.progress;
  }

  /**
   * Watch file processing progress for a specific operation.
   * Type-safe overload for download operations.
   *
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId Optional file ID for single file operations.
   *
   * @returns Observable of download progress updates.
   */
  public watchProgress(projectId: string, operation: 'download', sessionId: string, fileId?: string): Observable<IDownloadProgress>;

  /**
   * Watch file processing progress for a specific operation.
   * Type-safe overload for translation operations.
   *
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId Optional file ID for single file operations.
   *
   * @returns Observable of translation progress updates.
   */
  public watchProgress(projectId: string, operation: 'translation', sessionId: string, fileId?: string): Observable<ITranslationProgress>;

  /**
   * Watch file processing progress for a specific operation.
   * Type-safe overload for PDF export operations.
   *
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId Optional file ID for single file operations.
   *
   * @returns Observable of PDF export progress updates.
   */
  public watchProgress(projectId: string, operation: 'export-pdf', sessionId: string, fileId?: string): Observable<IPdfExportProgress>;

  /**
   * Watch file processing progress for a specific operation.
   * Generic overload for dynamic operation types.
   *
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId Optional file ID for single file operations.
   *
   * @returns Observable of file processing progress updates.
   */
  public watchProgress(projectId: string, operation: FileProcessingOperation, sessionId: string, fileId?: string): Observable<IFileProcessingProgress>;

  /**
   * Implementation of watchProgress.
   *
   * @param projectId The ID of the project containing the file.
   * @param operation The type of file processing operation (download, translation, or export-pdf).
   * @param sessionId The unique session identifier for this processing operation.
   * @param fileId Optional file ID for single file operations.
   *
   * @returns Observable of typed file processing progress updates.
   */
  public watchProgress(projectId: string, operation: FileProcessingOperation, sessionId: string, fileId?: string): Observable<TypedFileProcessingProgress> {
    return this.watchSSEProgress(projectId, operation, sessionId, fileId) as Observable<TypedFileProcessingProgress>;
  }

  /**
   * Stop watching file processing progress.
   *
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId Optional file ID for single file operations.
   */
  public stopWatching(projectId: string, operation: FileProcessingOperation, sessionId: string, fileId?: string): void {
    this.stopWatchingSSE(projectId, operation, sessionId, fileId);
  }

  /**
   * Build the SSE topic URL for file processing.
   *
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId
   *
   * @returns The topic URL path.
   */
  protected buildTopic(projectId: string, operation: FileProcessingOperation, sessionId: string, fileId?: string): string {
    const topicParams = new URLSearchParams({ sessionId });

    if (fileId) {
      topicParams.append('fileId', fileId);
    }

    return `/projects/${projectId}/files/processing-events/${operation}?${topicParams.toString()}`;
  }

  /**
   * Build the connection ID for file processing.
   *
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId
   *
   * @returns
   */
  protected buildConnectionId(projectId: string, operation: FileProcessingOperation, sessionId: string, fileId?: string): string {
    return `file-processing-${projectId}-${operation}-${sessionId}${fileId ? `-${fileId}` : ''}`;
  }

  /**
   * Hydrate progress data from SSE message.
   *
   * @param data
   * @param projectId
   * @param operation
   * @param sessionId
   * @param fileId
   *
   * @returns Hydrated progress object with operation-specific data.
   */
  protected hydrateProgressEvent(
    data: Partial<IFileProcessingProgress>,
    projectId: string,
    operation: FileProcessingOperation,
    sessionId: string,
    fileId?: string
  ): TypedFileProcessingProgress {
    // Generate consistent ID for the same operation
    const fallbackId = fileId || `${projectId}-${operation}-${sessionId}`;

    const baseProgress: IFileProcessingProgress = {
      id: data.id || fallbackId,
      status: data.status || FileProcessingStatus.IN_PROGRESS,
      processed: data.processed,
      total: data.total,
      filename: data.filename,
      error: data.error,
      projectId: data.projectId || projectId,
      operation: data.operation || operation,
      newFileId: data.newFileId
    };

    // Cast to any to access operation-specific data properties
    const dataWithOperationData = data as any;

    // Return typed progress based on operation
    switch (operation) {
      case 'download':
        return {
          ...baseProgress,
          operation: 'download',
          downloadData: dataWithOperationData.downloadData
        } as IDownloadProgress;

      case 'translation':
        return {
          ...baseProgress,
          operation: 'translation',
          translationData: dataWithOperationData.translationData
        } as ITranslationProgress;

      case 'export-pdf':
        return {
          ...baseProgress,
          operation: 'export-pdf',
          pdfExportData: dataWithOperationData.pdfExportData
        } as IPdfExportProgress;

      default:
        return baseProgress;
    }
  }
}
